import { CSSProperties, RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as LinkIcon } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import queryString from 'query-string';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from 'context/LayoutContext';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import { getStringValue, setQueryString } from 'utilities';
import useStyles from './styles';
import { TextContentProps, UseRevealEllipsisInTooltipHook, ValueWithLinkViewerProps } from './types';

const WRAP_TEXT_STYLE: CSSProperties = {
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'normal',
};

const TextContent = (props: TextContentProps) => {
  const { classes, isDarkMode, isExportMode, isTitle, name, shouldDisplayTooltip, textRef } = props;

  return (
    <Grid
      className={clsx(classes?.textContainer, { dark: isDarkMode })}
      style={isExportMode ? WRAP_TEXT_STYLE : undefined}>
      {isTitle ? (
        <Typography component="h3" variant="h4">
          {name}
        </Typography>
      ) : (
        <Tooltip placement="bottom-start" title={!isExportMode && shouldDisplayTooltip ? getStringValue(name) : ''}>
          <p className={classes?.text} ref={textRef} style={isExportMode ? WRAP_TEXT_STYLE : undefined}>
            {name}
          </p>
        </Tooltip>
      )}
    </Grid>
  );
};

const ValueWithLinkViewer = (props: ValueWithLinkViewerProps) => {
  const { exchangeRateMessage = '', isDarkMode = false, isTitle = false, name = '', url = '' } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [textWidth, setTextWidth] = useState(0);

  const textRef = useRef<HTMLParagraphElement | null>(null);

  const { isExportMode } = useLayoutContext();

  const classes = useStyles();

  const navigate = useNavigate();

  const { tooltipRef: textContainerRef, width: textContainerWidth }
    = useRevealEllipsisInTooltip() as unknown as UseRevealEllipsisInTooltipHook<HTMLAnchorElement | HTMLDivElement>;

  const shouldDisplayTooltip = useMemo(() => textWidth > textContainerWidth, [textContainerWidth, textWidth]);

  // Set Text width
  useLayoutEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, []);

  const handleHover = () => setIsHovered(true);

  const handleUnhover = () => setIsHovered(false);

  const redirectToUrl = () => {
    const parsedUrl = new URL(url, window.location.origin);
    const { pathname, search } = parsedUrl;

    const parsedQuery = queryString.parse(search);

    setQueryString({
      navigate,
      paramName: '',
      paramValue: '',
      pathname,
      searchParams: queryString.stringify(parsedQuery),
      shouldPushPath: true,
    });
  };

  return (
    <Tooltip placement="top-start" title={exchangeRateMessage} arrow>
      <Grid
        className={clsx(classes.container, { title: isTitle })}
        onClick={url ? redirectToUrl : undefined}
        onMouseEnter={url ? handleHover : undefined}
        onMouseLeave={url ? handleUnhover : undefined}
        ref={textContainerRef as RefObject<HTMLDivElement>}
        sx={url ? { cursor: 'pointer' } : undefined}>
        <TextContent
          classes={classes}
          isDarkMode={isDarkMode}
          isExportMode={isExportMode}
          isTitle={isTitle}
          name={name}
          shouldDisplayTooltip={shouldDisplayTooltip}
          textRef={textRef}
        />
        {url && !isExportMode && (
          <Grid
            className={clsx(classes.iconContainer, { active: isHovered, dark: isDarkMode })}
            id="company-info-icon-container">
            <LinkIcon className={clsx(classes.icon, { dark: isDarkMode })} />
          </Grid>
        )}
      </Grid>
    </Tooltip>
  );
};

export default ValueWithLinkViewer;
